import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import StreamIcon from "../../../assets/images/icons/stream.svg";
import EditIcon from "../../../assets/images/admin/edit-icon.svg";
import CategoryIcon from "../../../assets/images/admin/category-icon.svg";
import NotesIcon from "../../../assets/images/admin/notes-icon.svg";
import EntryModal from './EntryModal';
import MonthDropdown from "./MonthDropdown";
import * as actions from "../../../redux/actions/artistManager";
import { getUserPurchases } from "../../../redux/actions/billing";
import { useArtistManagerActivityTracker } from "../../../hooks/useArtistManagerActivityTracker";
import { useMetaCampaignAnalytics } from "../../../queries/growthEngine/metaCampaign.query";
import { formatFixedAmount } from "../../../helper/utility";

const expense_categories = [
    "Select",
    "Custom",
    "Expenses",
    "Streaming",
    "Marketing",
    "Content",
    "Merch",
    "Shows",
    "PR, Press & Sync",
    "Publishing",
    "Brand Deals",
    "Learning",
    "Operations",
    "Memberships",
    "Distributions",
];

export const LogExpenses = () => {
    const dispatch = useDispatch();
    const { trackExpenseLogged } = useArtistManagerActivityTracker();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [action, setAction] = useState(false);
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');

    // Memoize selectors to prevent unnecessary re-renders
    const { hasActiveSubscription, purchaseByMonth } = useSelector((state) => state.billing);
    const artistManagerReducer = useSelector((state) => state.artistManager);
    const authToken = useSelector((state) => state.auth.token);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const access = useSelector((state) => state.auth.access);
    const activeArtistProfileId = access?.artist?.activeArtistProfileId || null;
    const metaAccountKey =
        userDetails?.email || userDetails?.assignedArtistSongstateId || authToken || null;
    const metaCampaignUserKey = metaAccountKey
        ? `${metaAccountKey}:${activeArtistProfileId || "workspace"}`
        : null;
    const { data: metaCampaignAnalyticsData } = useMetaCampaignAnalytics({
        userKey: metaCampaignUserKey,
        range: "yearly",
        enabled: !!authToken && !!metaCampaignUserKey,
    });

    const getMonthlyData = useCallback((data, monthName, yr) => {
        if (!data?.timeline?.length || !monthName || !yr) return null;

        const monthIndex = new Date(`${monthName} 1, ${yr}`).getMonth();

        const filtered = data.timeline.filter((entry) => {
            const [entryYear, entryMonth] = String(entry.date || "").split("-");
            return Number(entryMonth) - 1 === monthIndex && Number(entryYear) === Number(yr);
        });

        if (!filtered.length) return null;

        const result = filtered.reduce(
            (acc, entry) => {
                acc.spend += entry.spend;
                acc.spendUsd += Number(entry.spendUsd) || 0;
                acc.impressions += entry.impressions;
                acc.clicks += entry.clicks;
                acc.cpc += entry.cpc;
                if (entry.currency) acc.currencies.add(entry.currency);
                return acc;
            },
            { spend: 0, spendUsd: 0, impressions: 0, clicks: 0, cpc: 0, currencies: new Set() }
        );

        result.spend = parseFloat(result.spend.toFixed(2));
        result.spendUsd = parseFloat(result.spendUsd.toFixed(2));
        result.currency = result.currencies.size === 1 ? [...result.currencies][0] : "USD";
        result.cpc = parseFloat((result.cpc / filtered.length).toFixed(2));
        delete result.currencies;

        return result;
    }, []);

    const adSpendThisMonth = useMemo(() => {
        if (!month || !year || !metaCampaignAnalyticsData) {
            return { spendUsd: 0, spend: 0, currency: "USD" };
        }
        const result = getMonthlyData(metaCampaignAnalyticsData, month, year);
        return {
            spendUsd: result?.spendUsd ?? 0,
            spend: result?.spend ?? 0,
            currency: result?.currency ?? "USD",
        };
    }, [metaCampaignAnalyticsData, month, year, getMonthlyData]);
    // Memoize derived values
    const expenses = useMemo(() =>
        artistManagerReducer?.loggedExpenses?.data?.expenses || [],
        [artistManagerReducer?.loggedExpenses?.data?.expenses]
    );

    const expenseById = useMemo(() =>
        artistManagerReducer?.expenseById || {},
        [artistManagerReducer?.expenseById]
    );

    const totalExpenses = useMemo(() =>
        artistManagerReducer?.loggedExpenses?.data?.totalAmount || 0,
        [artistManagerReducer?.loggedExpenses?.data?.totalAmount]
    );

    const totalSubscriptionAmount = useMemo(() =>
        purchaseByMonth?.statistics?.totalAmount || 0,
        [hasActiveSubscription, purchaseByMonth?.statistics?.totalAmount]
    );

    // total calculation
    const totalLoggedThisMonth = (Number(totalExpenses) || 0) + (Number(totalSubscriptionAmount) || 0) + (Number(adSpendThisMonth?.spendUsd) || 0)

    // Memoize callbacks
    const handleOpenModal = useCallback((e, id = 0) => {
        setIsModalOpen(true);
        if (e === "edit" && id !== 0) {
            setAction(e);
            dispatch(actions.fetchLogExpenseById(toast, id, () => {
                setIsLoading(false);
            }));
        } else {
            setAction(e);
        }
    }, [dispatch]);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const handleModalSubmit = useCallback((payload) => {
        if (hasActiveSubscription) {
            if (payload && payload.action === 'delete') {
                // delete flow
                dispatch(actions.deleteLogExpense(toast, payload.id, {}, month, year, setIsLoading, handleCloseModal));
                return;
            }

            const reqBody = {
                name: payload.name,
                amount: payload.amount,
                source: payload.source,
                category: payload.category,
                notes: payload.notes,
            };

            if (action === "edit") {
                dispatch(actions.updateLogExpense('', payload.id, reqBody, month, year, setIsLoading, handleCloseModal));
            } else {
                dispatch(actions.createLogExpense('', reqBody, month, year, setIsLoading, () => {
                    trackExpenseLogged();
                    handleCloseModal();
                }));
            }
        }
    }, [action, month, year, dispatch, handleCloseModal, trackExpenseLogged]);

    // Memoize month/year setters
    const handleMonthChange = useCallback((newMonth) => {
        setMonth(newMonth);
    }, []);

    const handleYearChange = useCallback((newYear) => {
        setYear(newYear);
    }, []);

    // Fetch expense data whenever month or year changes
    useEffect(() => {
        if (!month || !year) return;

        setIsLoading(true);
        Promise.all([
            dispatch(actions.fetchLogExpenses('', month, year, setIsLoading, "log_expense")),
            dispatch(getUserPurchases({ month, year }, "expenses"))
        ]).finally(() => {
            setIsLoading(false);
        });
    }, [month, year, dispatch, activeArtistProfileId]);

    // Memoize quick action cards
    const QuickActionCard = useCallback(({ title, icon, iconStyle = {} }) => (
        <div
            onClick={() => handleOpenModal("add")}
            className="bg-theme_clr2 w-[80px] h-[80px] rounded-xl text-center flex-1 cursor-pointer p-2"
        >
            <div className="name text-sm text-white mb-2">{title}</div>
            <div className="icon">
                {typeof icon === 'string' && icon.startsWith('bi-') ? (
                    <i className={`bi ${icon} text-3xl leading-none text-white`}></i>
                ) : (
                    <img src={icon} alt={`${title} Icon`} className="w-[30px] h-[30px] mx-auto" style={iconStyle} />
                )}
            </div>
        </div>
    ), [handleOpenModal]);

    return (
        <div className="info-box h-full bg-theme_clr1 font-inter font-medium flex flex-col rounded-xl text-[15px] text-theme_clr1 p-4 xl:p-5">
            <div className="header-section flex flex-wrap items-center justify-between gap-2 text-white mb-4">
                <div className="title-info text-[23px] font-bold font-manrope flex items-center gap-2 leading-tight">
                    <img
                        src={require('../../../assets/images/admin/Amplitude-Info.png')}
                        alt="Amplitude Info"
                        className="w-[30px] h-[30px]"
                    />
                    Log Expenses
                </div>
                <button
                    type="button"
                    onClick={() => handleOpenModal("add")}
                    className="btn-custom3"
                >
                    Add Entry <i className="bi bi-plus-circle text-base ms-1"></i>
                </button>
            </div>

            <div className="content-wrapper flex flex-col gap-2">
                <div className="bg-white rounded-xl p-4">
                    <div className="text-theme_clr1 text-sm mb-2">
                        Enter the details below to log expenses from music, merch, and more.
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <QuickActionCard title="Name" icon={EditIcon} />
                        <QuickActionCard title="Amount" icon="bi-currency-dollar" />
                        <QuickActionCard
                            title="Source"
                            icon={StreamIcon}
                            iconStyle={{ filter: "brightness(0) invert(1)" }}
                        />
                        <QuickActionCard title="Category" icon={CategoryIcon} />
                        <QuickActionCard title="Notes" icon={NotesIcon} />
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4">
                    <div className="wrapper mb-2">
                        <MonthDropdown
                            getMonth={handleMonthChange}
                            getYear={handleYearChange}
                        />
                    </div>
                    <div className="table-responsive max-h-[117px] overflow-auto">
                        {isLoading ? (
                            <div className="text-center text-theme_clr1 py-4">Loading...</div>
                        ) : expenses.length > 0 ? (
                            <table className="min-w-[450px] w-full text-left text-sm text-theme_clr1">
                                <thead>
                                    <tr>
                                        <th className="p-1">Name</th>
                                        <th className="p-1">Amount</th>
                                        <th className="p-1">Source</th>
                                        <th className="p-1">Category</th>
                                        <th className="p-1">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenses.map((expense) => (
                                        <tr
                                            key={expense._id}
                                            className="border-t hover:bg-orange-50 transition-colors"
                                        >
                                            <td className="p-1 font-medium">{expense.name}</td>
                                            <td className="p-1">${expense.amount}</td>
                                            <td className="p-1">{expense.source}</td>
                                            <td className="p-1">{expense.category}</td>
                                            <td className="p-1">
                                                <button
                                                    onClick={() => handleOpenModal("edit", expense._id)}
                                                    className="text-orange-600 font-semibold"
                                                >
                                                    View/Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center text-theme_clr1 py-4">
                                No Expense Logged for this month.
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white font-semibold flex flex-wrap justify-center gap-1 rounded-xl p-2">
                    <span>
                        Ad Spend (from Growth Engine): ${formatFixedAmount(adSpendThisMonth.spendUsd) ?? "0.00"}
                        {Number(formatFixedAmount(adSpendThisMonth.spend)) > 0 && adSpendThisMonth.currency
                            ? ` (${adSpendThisMonth.currency} ${formatFixedAmount(adSpendThisMonth.spend)})`
                            : ""}
                    </span>
                </div>
                <div className="bg-white font-semibold flex flex-wrap justify-center gap-1 rounded-xl p-2">
                    <span>UpNComer (Subscription + In App): ${totalSubscriptionAmount}</span>
                </div>
                <div className="bg-white font-semibold flex flex-wrap justify-center gap-1 rounded-xl p-2">
                    <span>Total Logged This Month: ${totalLoggedThisMonth}</span>
                </div>
            </div>

            <EntryModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleModalSubmit}
                defaultCategory={"Expenses"}
                categories={expense_categories}
                action={action}
                initialData={action === "edit" ? expenseById : {}}
            />
            {/* <ToastContainer /> */}
        </div>
    );
};